import {ChangeDetectionStrategy, Component, DestroyRef, Inject, Injectable, signal, Signal} from "@angular/core";
import {MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef} from "@angular/material/dialog";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {MatButtonModule} from "@angular/material/button";
import {AksjeeierbokId, AksjeeierbokModell, OpprettAksjeeiereOgBeholdningerSpesifikasjon, Organisasjonsnummer, SkatteetatenAksjeeierbokModellResponse, SkatteetatenAksjeeierbokRessursClient, SkatteetatenAksjeeierbokSammendragModell} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {MatToolbarModule} from "@angular/material/toolbar";
import {MatIconModule} from "@angular/material/icon";
import {VisSpinnerDirektiv} from "web-ng2/delt/direktiver/button--visSpinner/button--visSpinner";
import {TranslateModule} from "@ngx-translate/core";
import {lesPlainDateSerialisert} from "felles-ng2/støttefunksjoner/temporal";
import {OpprettAksjeeiereFraSkatteetatenListeKomponent, OpprettAksjeeiereFraSkatteetatenModell} from "../../komponenter/opprett-aksjeeiere-fra-skatteetaten-liste/opprett-aksjeeiere-fra-skatteetaten-liste";
import {LukkAbonnementer} from "felles-ng2/infrastruktur/rxjs/LukkAbonnementer";
import {Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {Temporal} from "temporal-polyfill";
import {lagrerSignal} from "felles-ng2/tjenester/LagrerIndikator";
import {AksjeeierDatatjeneste} from "felles-ng2/tjenester/datalager/AksjeeierDatatjeneste";
import {VisDersomIkkeScrolletTilBunnDirektiv} from "web-ng2/delt/direktiver/visDersomIkkeScrolletTilBunn/visDersomIkkeScrolletTilBunn";
import PlainDate = Temporal.PlainDate;

const debug = initDebugger('OpprettAksjeeiereFraSkatteetatenDialogKomponent');

export interface DialogParametre {
    skatteetatenAksjeeierbokSammendragModell:SkatteetatenAksjeeierbokSammendragModell;
    aksjeeierbokId: AksjeeierbokId;
}

@Component({
    selector: 'opprett-aksjeeiere-fra-skatteetaten-dialog',
    templateUrl: 'opprett-aksjeeiere-fra-skatteetaten-dialog.html',
    styleUrls: ['opprett-aksjeeiere-fra-skatteetaten-dialog.scss'],
    imports: [
        FormsModule,
        MatButtonModule,
        MatDialogModule,
        MatIconModule,
        MatToolbarModule,
        VisSpinnerDirektiv,
        ReactiveFormsModule,
        TranslateModule,
        OpprettAksjeeiereFraSkatteetatenListeKomponent,
        VisDersomIkkeScrolletTilBunnDirektiv
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OpprettAksjeeiereFraSkatteetatenDialogKomponent {

    static  vis(dialog: MatDialog, skatteetatenAksjeeierbokSammendragModell:SkatteetatenAksjeeierbokSammendragModell, aksjeeierbokId: AksjeeierbokId): MatDialogRef<OpprettAksjeeiereFraSkatteetatenDialogKomponent, AksjeeierbokModell|null> {
        return dialog.open(OpprettAksjeeiereFraSkatteetatenDialogKomponent, {data: {skatteetatenAksjeeierbokSammendragModell: skatteetatenAksjeeierbokSammendragModell, aksjeeierbokId: aksjeeierbokId} as DialogParametre, disableClose: true, ariaLabelledBy: 'import-skatteetatet-tittel', width: '1400px'});
    }

    private ab = new LukkAbonnementer();

    private readonly aksjeeierbokId:AksjeeierbokId

    skjema = OpprettAksjeeiereFraSkatteetatenListeKomponent.opprettSkjema();
    // gruppe: Signal<GruppeModell>;
    lagrer = lagrerSignal();

    sammendrag :Signal<SkatteetatenAksjeeierbokSammendragModell>;
    // skattedato brukes ikke når vi laster.
    modell = signal(new OpprettAksjeeiereFraSkatteetatenModell('laster', this.skjema, null, this.ab, this.oversettelser, PlainDate.from('2023-12-31')));

    constructor( @Inject(MAT_DIALOG_DATA) parametre: DialogParametre,private destroyRef: DestroyRef, private avhengigheter: OpprettAksjeeiereFraSkatteetatenDialogKomponentAvhengigheter,private oversettelser:Oversettelser,
                public dialogRef: MatDialogRef<OpprettAksjeeiereFraSkatteetatenDialogKomponent>) {
        this.ab.registrerHosDestroyRef(this.destroyRef);
        this.sammendrag = signal(parametre.skatteetatenAksjeeierbokSammendragModell);
        this.aksjeeierbokId = parametre.aksjeeierbokId;
        this.avhengigheter.hent(parametre.skatteetatenAksjeeierbokSammendragModell.orgnummer).then(response => {
            if (erNil(response.aksjeeierbokModell)) {
                this.modell.set(new OpprettAksjeeiereFraSkatteetatenModell('ikkeFunnet', this.skjema, null, this.ab, this.oversettelser,lesPlainDateSerialisert(response.skatteetatenDato)));
                return;
            }
            console.log(response)
            this.modell.set(new OpprettAksjeeiereFraSkatteetatenModell('hentet', this.skjema, response.aksjeeierbokModell, this.ab, this.oversettelser,lesPlainDateSerialisert(response.skatteetatenDato)));

        }).catch(e => {
            console.log("FEIL",e);
            // skattedato brukes ikke når vi har feil.
            this.modell.set(new OpprettAksjeeiereFraSkatteetatenModell('feilet', this.skjema, null, this.ab, this.oversettelser, PlainDate.from('2023-12-31')));
        })
    }

    avbryt(): void {
        this.dialogRef.close(null);
    }

    async lagre() {
        const aksjeeiermodell = await this.lagrer.utfør(async () => {
            return this.avhengigheter.lagre(this.aksjeeierbokId, this.modell().lagSpekk())
        });
        this.dialogRef.close(aksjeeiermodell);
    }

    //private hjelpemetoder

}


@Injectable()
export class OpprettAksjeeiereFraSkatteetatenDialogKomponentAvhengigheter {


    constructor(private skatteetatenAksjeeierbokRessurs: SkatteetatenAksjeeierbokRessursClient, private aksjeeierDatatjeneste:AksjeeierDatatjeneste) {
    }

    hent(orgnummer: Organisasjonsnummer): Promise<SkatteetatenAksjeeierbokModellResponse> {
        return this.skatteetatenAksjeeierbokRessurs.finn(orgnummer).somPromise();
    }

    lagre(aksjeeierbokId: AksjeeierbokId, opprettAksjeeiereOgBeholdningerSpesifikasjon: OpprettAksjeeiereOgBeholdningerSpesifikasjon): Promise<any> {
        return this.aksjeeierDatatjeneste.opprettAksjeeiereOgBeholding(aksjeeierbokId, opprettAksjeeiereOgBeholdningerSpesifikasjon);
    }
}
